import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  RefreshCw, Wallet, ReceiptText, CalendarRange, ShoppingBag, XCircle,
  DollarSign, ArrowRight, Package, AlertTriangle, RefreshCcw, MessageSquare, Ban,
} from "lucide-react";
import { Card, Btn } from "@/components/admin/ui";
import { API } from "@/config";
import { CashflowChart } from "@/pages/admin/CashflowChart";
import { PomRibbonStats } from "@/pages/admin/PomRibbonStats";
import { CancelReasonStats } from "@/pages/admin/CancelReasonStats";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDates = (a, b) => (a && b) ? `${a} → ${b}` : (a || b || "—");

// Colored status pill (matches the BabyQuip "PAID" chip on the right of each row).
const StatusPill = ({ payment_status, status }) => {
  const cancelled = status === "cancelled";
  const paid = payment_status === "paid" && !cancelled;
  const label = cancelled ? "Cancelled" : paid ? "Paid" : (status || payment_status || "").replace(/_/g, " ");
  const cls = cancelled
    ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
    : paid
      ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/50"
      : "bg-amber-500/15 text-amber-300 border-amber-500/40";
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-widest border font-semibold ${cls}`}>
      {label}
    </span>
  );
};

// Single KPI tile — number on top-left, label + period below. Colored underline
// gives each metric visual identity without shouting.
const KpiTile = ({ icon: Icon, value, label, period, tone, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={testId}
    className={`text-left group ${onClick ? "cursor-pointer" : "cursor-default"}`}
  >
    <Card className={`p-5 h-full transition-all ${onClick ? "group-hover:-translate-y-0.5 group-hover:border-[#FFD166]/50" : ""}`}>
      <div className="flex items-start justify-between">
        <div className="font-serif text-3xl leading-none text-[#F6F1E4] tabular-nums">{value}</div>
        <span className={`w-8 h-8 rounded-full grid place-items-center border ${tone}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
      </div>
      <div className="mt-3 text-sm font-medium text-[#F6F1E4]/90">{label}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#B9C8D5] mt-0.5">{period}</div>
    </Card>
  </button>
);

// Reservation mini-table used four times on the dashboard. Compact row layout
// with a colored status chip pinned to the right edge (BabyQuip parity).
const ReservationList = ({ title, items, emptyLabel, viewAllHref, testId, onRowClick }) => {
  const nav = useNavigate();
  return (
    <Card className="p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-xl text-[#F6F1E4]">{title}</h3>
        {viewAllHref && (
          <button
            type="button"
            onClick={() => nav(viewAllHref)}
            className="text-[10px] uppercase tracking-[0.2em] text-[#FFD166] hover:text-[#F4A93A] inline-flex items-center gap-1"
            data-testid={`${testId}-viewall`}
          >
            View all <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#B9C8D5] italic py-6 text-center">{emptyLabel}</div>
      ) : (
        <>
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center px-2 pb-2 text-[10px] uppercase tracking-[0.18em] text-[#B9C8D5]/70">
            <span>Customer</span>
            <span>Dates</span>
            <span className="text-right">Payout</span>
            <span />
          </div>
          <div className="space-y-1.5">
            {items.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onRowClick?.(r)}
                className="w-full grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center bg-black/25 border border-[#FFD166]/10 hover:border-[#FFD166]/40 hover:bg-black/40 transition-colors rounded-lg px-2 py-2 text-left"
                data-testid={`${testId}-row-${r.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-[#F6F1E4] font-medium truncate">{r.customer_name || "—"}</div>
                  <div className="text-[10px] font-mono text-[#FFD166]/70">{r.short_code || `#${(r.id || "").slice(0, 6).toUpperCase()}`}</div>
                </div>
                <span className="text-[11px] text-[#B9C8D5] tabular-nums whitespace-nowrap">{fmtDates(r.delivery_date, r.pickup_date)}</span>
                <span className="text-sm font-medium text-[#F6F1E4] tabular-nums whitespace-nowrap text-right">{money(r.payout)}</span>
                <StatusPill payment_status={r.payment_status} status={r.status} />
              </button>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

export const Dashboard = () => {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [sweep, setSweep] = useState(null);
  const [refundQueue, setRefundQueue] = useState(null);
  const [changeQueue, setChangeQueue] = useState(null);
  const [attnCounts, setAttnCounts] = useState(null);

  const load = useCallback(async () => {
    const [dash, dry, rq, cq, ac] = await Promise.all([
      axios.get(`${API}/admin/dashboard`),
      axios.post(`${API}/admin/payouts/run`, null, { params: { dry_run: true } }),
      axios.get(`${API}/admin/refund-queue`),
      axios.get(`${API}/admin/change-requests`),
      axios.get(`${API}/admin/notifications/counts`).catch(() => ({ data: { action_total: 0, by_category: {} } })),
    ]);
    setD(dash.data);
    setSweep({
      total_cents: dry.data.would_process_total_cents || 0,
      count: dry.data.would_process_count || 0,
    });
    const reqs = rq.data?.requests || [];
    const total = reqs.reduce((sum, r) => sum + Number(r.pending_refund_request?.refund_amount ?? r.refund_amount ?? 0), 0);
    setRefundQueue({ count: reqs.length, total });
    setChangeQueue({ count: (cq.data?.requests || []).length });
    setAttnCounts(ac.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="text-[#B9C8D5]">Loading…</div>;

  const goRow = (r) => nav(`/admin/reservations?open=${r.id}`);

  const monthTiles = [
    { icon: ShoppingBag, value: d.orders_month, label: "Total Orders", tone: "border-emerald-400/50 text-emerald-300 bg-emerald-500/10", href: `/admin/reservations?from=${d.month_start}&tab=paid`, testId: "kpi-orders-month" },
    { icon: XCircle, value: d.cancellations_month, label: "Cancellations", tone: "border-rose-400/50 text-rose-300 bg-rose-500/10", href: `/admin/reservations?from=${d.month_start}&status=cancelled`, testId: "kpi-cancellations-month" },
    { icon: DollarSign, value: money(d.volume_month), label: "Order Volume", tone: "border-[#FFD166]/60 text-[#FFD166] bg-[#FFD166]/10", href: `/admin/reservations?from=${d.month_start}`, testId: "kpi-volume-month" },
  ];
  const yearTiles = [
    { icon: ShoppingBag, value: d.orders_year, label: "Total Orders", tone: "border-emerald-400/50 text-emerald-300 bg-emerald-500/10", href: `/admin/reservations?from=${d.year_start}&tab=paid`, testId: "kpi-orders-year" },
    { icon: XCircle, value: d.cancellations_year, label: "Cancellations", tone: "border-rose-400/50 text-rose-300 bg-rose-500/10", href: `/admin/reservations?from=${d.year_start}&status=cancelled`, testId: "kpi-cancellations-year" },
    { icon: DollarSign, value: money(d.volume_year), label: "Order Volume", tone: "border-[#FFD166]/60 text-[#FFD166] bg-[#FFD166]/10", href: `/admin/reservations?from=${d.year_start}`, testId: "kpi-volume-year" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl">Dashboard</h1>
          <p className="text-sm text-[#B9C8D5] mt-1">
            {d.today_deliveries} deliveries · {d.today_pickups} pickups today
          </p>
        </div>
        <Btn variant="ghost" onClick={load}><RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
      </div>

      {/* Alert stack: tonight's payout, refund queue, date-change requests */}
      {sweep && sweep.total_cents > 0 && (
        <button type="button" onClick={() => nav("/admin/payouts")} className="w-full text-left mb-4 group" data-testid="dash-payout-tonight">
          <Card className="p-4 md:p-5 border-[#FFD166]/30 group-hover:border-[#FFD166]/60 transition-all">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-full bg-[#FFD166]/15 border border-[#FFD166]/40 grid place-items-center shrink-0"><Wallet className="w-5 h-5 text-[#FFD166]" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.22em] text-[#B9C8D5]">Tonight's Stripe sweep · 1 AM ET</div>
                <div className="font-serif text-3xl text-[#FFD166] leading-tight" data-testid="payout-tonight-amount">${(sweep.total_cents / 100).toFixed(2)}</div>
                <div className="text-[11px] text-[#B9C8D5] mt-0.5">{sweep.count} payout {sweep.count === 1 ? "entry" : "entries"} scheduled — tap to review or cancel</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#FFD166]/80 group-hover:text-[#FFD166] hidden sm:block">Open payouts →</div>
            </div>
          </Card>
        </button>
      )}
      {refundQueue && refundQueue.count > 0 && (
        <button type="button" onClick={() => nav("/admin/inbox?tab=refunds")} className="w-full text-left mb-4 group" data-testid="dash-refund-queue">
          <Card className="p-4 md:p-5 border-amber-500/40 group-hover:border-amber-500/70 transition-all">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/40 grid place-items-center shrink-0"><ReceiptText className="w-5 h-5 text-amber-300" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.22em] text-[#B9C8D5]">Refund queue · needs your sign-off</div>
                <div className="font-serif text-3xl text-amber-300 leading-tight" data-testid="refund-queue-amount">${refundQueue.total.toFixed(2)}</div>
                <div className="text-[11px] text-[#B9C8D5] mt-0.5">{refundQueue.count} refund {refundQueue.count === 1 ? "request" : "requests"} · tap to approve or deny each one</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300/80 group-hover:text-amber-300 hidden sm:block">Open queue →</div>
            </div>
          </Card>
        </button>
      )}
      {changeQueue && changeQueue.count > 0 && (
        <button type="button" onClick={() => nav("/admin/inbox?tab=changes")} className="w-full text-left mb-4 group" data-testid="dash-change-requests">
          <Card className="p-4 md:p-5 border-sky-500/40 group-hover:border-sky-500/70 transition-all">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="w-10 h-10 rounded-full bg-sky-500/15 border border-sky-500/40 grid place-items-center shrink-0"><CalendarRange className="w-5 h-5 text-sky-300" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.22em] text-[#B9C8D5]">Date change requests · awaiting your decision</div>
                <div className="font-serif text-3xl text-sky-300 leading-tight" data-testid="change-requests-count">{changeQueue.count}</div>
                <div className="text-[11px] text-[#B9C8D5] mt-0.5">{changeQueue.count === 1 ? "customer wants" : "customers want"} to move their trip dates · tap to approve or deny</div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-sky-300/80 group-hover:text-sky-300 hidden sm:block">Open queue →</div>
            </div>
          </Card>
        </button>
      )}

      {/* ── Needs Attention (from admin_notifications) ──────────────────── */}
      {attnCounts && attnCounts.action_total > 0 && (
        <div className="mb-5" data-testid="dash-needs-attention">
          <Card className="p-4 md:p-5 border-red-500/30">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-xs uppercase tracking-[0.2em] text-red-400 font-semibold">Needs Attention</span>
              </div>
              <button
                type="button"
                onClick={() => nav("/admin/notifications?action=true")}
                className="text-xs text-[#FFD166] hover:underline"
                data-testid="dash-needs-attention-view-all"
              >
                {attnCounts.action_total} total → View all
              </button>
            </div>
            <div className="space-y-1.5">
              {attnCounts.by_category?.change_request > 0 && (
                <button
                  type="button"
                  onClick={() => nav("/admin/notifications?category=change_request&action=true")}
                  className="w-full flex items-center justify-between rounded-lg px-3 py-2 hover:bg-[#FFD166]/5 transition-colors group"
                  data-testid="dash-attn-change-request"
                >
                  <div className="flex items-center gap-2">
                    <RefreshCcw className="w-4 h-4 text-[#FFD166]" />
                    <span className="text-sm text-[#F6F1E4]">Change Requests</span>
                  </div>
                  <span className="text-[#FFD166] font-bold text-sm">{attnCounts.by_category.change_request} →</span>
                </button>
              )}
              {attnCounts.by_category?.cancellation > 0 && (
                <button
                  type="button"
                  onClick={() => nav("/admin/notifications?category=cancellation&action=true")}
                  className="w-full flex items-center justify-between rounded-lg px-3 py-2 hover:bg-red-500/5 transition-colors"
                  data-testid="dash-attn-cancellation"
                >
                  <div className="flex items-center gap-2">
                    <Ban className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-[#F6F1E4]">Cancellation Requests</span>
                  </div>
                  <span className="text-red-400 font-bold text-sm">{attnCounts.by_category.cancellation} →</span>
                </button>
              )}
              {attnCounts.by_category?.payment_issue > 0 && (
                <button
                  type="button"
                  onClick={() => nav("/admin/notifications?category=payment_issue&action=true")}
                  className="w-full flex items-center justify-between rounded-lg px-3 py-2 hover:bg-orange-500/5 transition-colors"
                  data-testid="dash-attn-payment"
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-400" />
                    <span className="text-sm text-[#F6F1E4]">Payment Issues</span>
                  </div>
                  <span className="text-orange-400 font-bold text-sm">{attnCounts.by_category.payment_issue} →</span>
                </button>
              )}
              {attnCounts.by_category?.failed_message > 0 && (
                <button
                  type="button"
                  onClick={() => nav("/admin/notifications?category=failed_message&action=true")}
                  className="w-full flex items-center justify-between rounded-lg px-3 py-2 hover:bg-red-500/5 transition-colors"
                  data-testid="dash-attn-failed-messages"
                >
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-red-400" />
                    <span className="text-sm text-[#F6F1E4]">Failed Messages</span>
                  </div>
                  <span className="text-red-400 font-bold text-sm">{attnCounts.by_category.failed_message} →</span>
                </button>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* KPI grid — This Month */}
      <div className="mt-2 mb-2 text-[11px] uppercase tracking-[0.2em] text-[#B9C8D5]">This Month</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {monthTiles.map((t) => (
          <KpiTile key={t.testId} icon={t.icon} value={t.value} label={t.label} period="This month" tone={t.tone} testId={t.testId} onClick={() => nav(t.href)} />
        ))}
      </div>

      {/* KPI grid — This Year */}
      <div className="mt-6 mb-2 text-[11px] uppercase tracking-[0.2em] text-[#B9C8D5]">This Year</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {yearTiles.map((t) => (
          <KpiTile key={t.testId} icon={t.icon} value={t.value} label={t.label} period="This year" tone={t.tone} testId={t.testId} onClick={() => nav(t.href)} />
        ))}
      </div>

      {/* Reservation shortlists */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ReservationList
          title="Up Next"
          items={d.up_next || []}
          emptyLabel="No upcoming deliveries scheduled."
          viewAllHref="/admin/reservations"
          testId="dash-up-next"
          onRowClick={goRow}
        />
        <ReservationList
          title="Ending Soon"
          items={d.ending_soon || []}
          emptyLabel="Nothing ending in the near future."
          viewAllHref="/admin/reservations"
          testId="dash-ending-soon"
          onRowClick={goRow}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ReservationList
          title="In Progress"
          items={d.in_progress || []}
          emptyLabel="No live rentals right now."
          viewAllHref="/admin/live"
          testId="dash-in-progress"
          onRowClick={goRow}
        />
        <ReservationList
          title="Recently Booked"
          items={d.recently_booked || []}
          emptyLabel="No bookings yet."
          viewAllHref="/admin/reservations"
          testId="dash-recently-booked"
          onRowClick={goRow}
        />
      </div>

      <div className="mt-6">
        <CashflowChart days={30} />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PomRibbonStats days={30} />
        <CancelReasonStats days={90} />
      </div>
    </div>
  );
};
